import React, { createContext, useContext, useState } from 'react';
import { Notification, Box } from '@mantine/core';
import { IconX, IconCheck, IconExclamationMark, IconInfoCircle } from '@tabler/icons-react';
import { useMediaQuery } from '@mantine/hooks';
import { EnumTipoNotificacion } from '../interfaces';

interface DatosNotificacion {
    id: string;
    tipo: EnumTipoNotificacion;
    titulo: string;
    mensaje: string;
    duracion?: number;
}

interface TipoContextoNotificacion {
    mostrarNotificacion: (
        tipo: EnumTipoNotificacion,
        titulo: string,
        mensaje: string,
        duracion?: number,
        persistente?: boolean // 👈 nuevo opcional
    ) => string;
    removerNotificacion: (id: string) => void; // 👈 nuevo
}

const ContextoNotificacion = createContext<TipoContextoNotificacion | undefined>(undefined);

interface PropsProveedorNotificacion {
    children: React.ReactNode;
}

export const ProveedorNotificacion: React.FC<PropsProveedorNotificacion> = ({ children }) => {
    const [notificaciones, setNotificaciones] = useState<DatosNotificacion[]>([]);
    const esMovil = useMediaQuery('(max-width: 768px)');

    const removerNotificacion = (id: string) => {
        setNotificaciones(prev => prev.filter(notificacion => notificacion.id !== id));
    };

    const mostrarNotificacion = (
        tipo: EnumTipoNotificacion,
        titulo: string,
        mensaje: string,
        duracion: number = 4000,
        persistente: boolean = false
    ): string => {

        const id = Date.now().toString();

        const nuevaNotificacion: DatosNotificacion = {
            id,
            tipo,
            titulo,
            mensaje,
            duracion
        };

        setNotificaciones(prev => [...prev, nuevaNotificacion]);

        // 👇 Solo auto-remueve si NO es persistente
        if (!persistente) {
            setTimeout(() => {
                removerNotificacion(id);
            }, duracion);
        }

        return id;
    };

    const obtenerIcono = (tipo: EnumTipoNotificacion) => {
        switch (tipo) {
            case EnumTipoNotificacion.Success:
                return <IconCheck size={20} />;
            case EnumTipoNotificacion.Error:
                return <IconX size={20} />;
            case EnumTipoNotificacion.Warning:
                return <IconExclamationMark size={20} />;
            case EnumTipoNotificacion.Information:
                return <IconInfoCircle size={20} />;
            default:
                return <IconInfoCircle size={20} />;
        }
    };

    const obtenerColor = (tipo: EnumTipoNotificacion) => {
        switch (tipo) {
            case EnumTipoNotificacion.Success:
                return '#2CBA02';
            case EnumTipoNotificacion.Error:
                return '#BA0202';
            case EnumTipoNotificacion.Warning:
                return '#BA9202';
            case EnumTipoNotificacion.Information:
                return 'primary';
            default:
                return 'primary';
        }
    };

    return (
        <ContextoNotificacion.Provider value={{ mostrarNotificacion, removerNotificacion }}>
            {children}

            <Box
                style={{
                    position: 'fixed',
                    top: '20px',
                    right: '20px',
                    zIndex: 99999999,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    maxWidth: '400px',
                    width: esMovil ? 'calc(100% - 40px)' : 'auto'
                }}
            >
                {notificaciones.map((notificacion) => (
                    <Notification
                        key={notificacion.id}
                        icon={obtenerIcono(notificacion.tipo)}
                        color={obtenerColor(notificacion.tipo)}
                        title={notificacion.titulo}
                        onClose={() => removerNotificacion(notificacion.id)}
                        style={{
                            animation: 'slideInRight 0.3s ease-out'
                        }}
                    >
                        {notificacion.mensaje}
                    </Notification>
                ))}
            </Box>

            <style>
                {`
                @keyframes slideInRight {
                    from {
                        transform: translateX(100%);
                        opacity: 0;
                    }
                    to {
                        transform: translateX(0);
                        opacity: 1;
                    }
                }
                `}
            </style>
        </ContextoNotificacion.Provider>
    );
};

export const useNotificaciones = (): TipoContextoNotificacion => {
    const contexto = useContext(ContextoNotificacion);
    if (!contexto) {
        throw new Error('useNotificaciones debe ser usado dentro de ProveedorNotificacion');
    }
    return contexto;
};